import { MainTracker } from "../PixelTrackers/MainTracker";

/**
 * "Test Lab Multi Test AB" (DE): A = control (one test kit per order, limit popup), B = several kits
 * per order. The arm is normally rolled once on the CMS pages (ab_tests_script.ejs) and mirrored in a
 * parent-domain cookie, because the funnel runs on another origin and can't see the CMS storage.
 * A visitor who enters the funnel directly is rolled by the api on the shipping page instead; the
 * funnel then writes the same cookie so the CMS pages adopt that arm later.
 */
export const TEST_LAB_MULTI_TEST_COOKIE = "test_lab_multi_test_ab";
export const TEST_LAB_MULTI_TEST_TRACKING_PROP = "Test Lab Multi Test AB";
export const TEST_LAB_MULTI_TEST_EVENT = "Test Lab Multi Test AB";

export type TestLabMultiTestVariant = "A" | "B";

const isVariant = (value: unknown): value is TestLabMultiTestVariant => value === "A" || value === "B";

export const readTestLabMultiTestVariant = (): TestLabMultiTestVariant | null => {
  if (typeof document === "undefined") return null;
  try {
    const raw = document.cookie.split("; ").find((cookie) => cookie.startsWith(`${TEST_LAB_MULTI_TEST_COOKIE}=`));
    if (!raw) return null;
    const value = decodeURIComponent(raw.slice(TEST_LAB_MULTI_TEST_COOKIE.length + 1)).toUpperCase();
    return isVariant(value) ? value : null;
  } catch {
    return null;
  }
};

/** Same parent-domain cookie the CMS pages write, so both origins see one arm. */
export const writeTestLabMultiTestCookie = (variant: TestLabMultiTestVariant): void => {
  if (typeof document === "undefined") return;
  try {
    const host = window.location.hostname;
    const domain = host.includes(".") ? `; domain=.${host.split(".").slice(-2).join(".")}` : "";
    document.cookie = `${TEST_LAB_MULTI_TEST_COOKIE}=${variant}; path=/; max-age=31536000; SameSite=Lax${domain}`;
  } catch {
    // ignore
  }
};

/**
 * The cookie arm wins over the server's per-country switch: a cart view built by a request that did not carry
 * the arm (coupon, subscription, risk group, delivery option...) answers with the switch, and a B value there
 * would re-enable the "+" on a kit for an A visitor. Applied to every cart state write (useCart).
 */
export const applyTestLabMultiTestArm = <T extends { allow_multiple_test_lab_kits?: boolean }>(cart: T): T => {
  const variant = readTestLabMultiTestVariant();
  if (!variant) return cart;
  const allowMultipleKits = variant === "B";
  return cart.allow_multiple_test_lab_kits === allowMultipleKits ? cart : { ...cart, allow_multiple_test_lab_kits: allowMultipleKits };
};

/** The arm to use: the one the api answered with when known, otherwise the cookie. */
export const resolveTestLabMultiTestVariant = (serverVariant?: string | null): TestLabMultiTestVariant | null =>
  isVariant(serverVariant) ? serverVariant : readTestLabMultiTestVariant();

/** Property for the shipping page view (and any other event): absent when the visitor is not in the test. */
export const getTestLabMultiTestTrackingProps = (
  serverVariant?: string | null,
): Partial<Record<typeof TEST_LAB_MULTI_TEST_TRACKING_PROP, TestLabMultiTestVariant>> => {
  const variant = resolveTestLabMultiTestVariant(serverVariant);
  return variant ? { [TEST_LAB_MULTI_TEST_TRACKING_PROP]: variant } : {};
};

/** Super property, so every funnel event of a bucketed visitor carries the arm. No-op when unbucketed. */
export const registerTestLabMultiTestSuperProperty = (serverVariant?: string | null): void => {
  const props = getTestLabMultiTestTrackingProps(serverVariant);
  if (!props[TEST_LAB_MULTI_TEST_TRACKING_PROP]) return;
  MainTracker.superProperties(props);
};

/** Assignment event, sent once: only when the api rolled the arm for a visitor who skipped the CMS pages. */
export const trackTestLabMultiTestAssignment = (variant: TestLabMultiTestVariant): void => {
  MainTracker.track("custom", TEST_LAB_MULTI_TEST_EVENT, { variant });
};
